import { Settings, Stop, Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, Grid, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import millisecondsToMinutes from 'date-fns/millisecondsToMinutes';
import { getTimezoneOffset } from 'date-fns-tz';
import { useState } from 'react';
import {
    ComponentBodyViewTopItems,
    ComponentLoadingStateTableItem,
    DashboardCardComponent,
    MobileDashboardCard,
    MobileDashboard,
    BaseDashboardTab
} from '../dashboard/views';
import { SensorsPlacements, Sensors, SensorSparkline, SensorsStatus, SensorStatus } from '../shared/sensors/models';
import shared_sensors_utils from '../shared/sensors/util';
import { BaseToolbarNavPageLayout } from '../../backbone/shared/views/baseLayoutViews';
import {
    Cooldown,
    Cooldowns,
    Gateways,
    OperatorIds,
    SensorReading,
    SensorTriggers,
    SettingTrigger,
    SettingTriggers,
    TriggersWithColors
} from './models';
import {
    confirmNotification,
    hideLoadingDialog,
    showOfflineMessage,
    showSuccessMessage,
    showLoadingDialog,
    showErrorMessage
} from '../../shared/utils/notifications';
import { DashboardViewConfigurations } from '../dashboard/models';
import { get_device_status_text, get_device_status_class } from './sensors';
import { BaseTypeaheadModal, MobileBaseTypeahead } from '../../backbone/shared/views/typeaheads';
import { EmptyListMessage } from '../../backbone/shared/views/EmptyListMessage';
import { DeviceNameStepView } from '../../backbone/features/SetupDeviceWizard/DeviceNameStepView/view';
import { GatewayTypeStepView } from '../../backbone/features/SetupDeviceWizard/GatewayTypeStepView/view';
import { GatewayMessageStepView } from '../../backbone/features/SetupDeviceWizard/GatewayTypeStepView/GatewayMessageStepView/view';

import { PlacementStepView } from '../../backbone/features/SetupDeviceWizard/PlacementStepView/view';
import { DeviceBarcodeStepView } from '../../backbone/features/SetupDeviceWizard/DeviceBarcodeStepView/view';
import { isConnected } from '../../shared/utils/is';

const PLOT_BAND_UPPER_LIMIT = 1000;
const PLOT_BAND_LOWER_LIMIT = -1000;

const DEFAULT_PLOT_LINE_OPTIONS = {
    width: 2,
    dashStyle: 'ShortDash',
    zIndex: 2
};

const PLOT_BAND_Z_INDEX = 1;

export const CooldownItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_cooldown_row',
    tagName: 'tr',
    model: null, // Cooldown
    events: {
        click: 'display_cooldown_details_page'
    },

    templateHelpers: function () {
        return {
            sensor_name: this.model.get('sensor').get('name'),
            status_display: this.model.get_cooldown_status_for_display()
        };
    },

    display_cooldown_details_page: function () {
        $f.mobile.nav.show(CooldownDetailPage, {
            model: this.model
        });
    }
});

export const CooldownsList = ComponentBodyViewTopItems.extend({
    itemView: CooldownItem,
    collection: null, // Cooldowns
    show_default_four_weeks_header: false
});

export const SensorItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_sensor_row',
    tagName: 'tr',
    model: null, // Sensor

    events: {
        click: 'display_sensor_detail'
    },

    display_sensor_detail: function () {
        $f.mobile.nav.show(SensorDetailPage, {
            model: this.model,
            sparkline: this.sensor_sparkline
        });
    },

    templateHelpers: function () {
        var readings = this.sensor_sparkline.get('values');
        var sparkline_values = $f.mobile.sensors.util.convert_graph_readings_to_company_temperature(readings);

        return {
            last_reading_date_formatted: this.model.is_initializing()
                ? _t('mobile_sensors_initializing')
                : this.model.get_last_reading_timedelta_str(),
            last_reading_value_formatted: this.model.get_last_reading_value(),
            sparkline_values: sparkline_values,
            placement: this.model.get_placement_for_display(),
            equipment_location: this.getEquipmentLocationTitle(),
            sensor_status: get_device_status_text(this.model.get('is_enabled'), this.model.get('is_online')),
            sensor_status_class: get_device_status_class(this.model.get('is_enabled'), this.model.get('is_online')),
            show_equipment: this.showEquipment(),
            show_status: this.show_sensor()
        };
    },

    initialize: function (options) {
        this._fetch_sparkline_data();
    },

    onRender: function () {
        this.render_chart();
    },

    show_sensor: function () {
        return (
            get_device_status_text(this.model.get('is_enabled'), this.model.get('is_online')) !==
            _t('mobile_sensors_device_online')
        );
    },

    showEquipment: function () {
        return $f.active_company.has_feature('sensor_equipment_readings');
    },

    getEquipmentLocationTitle: function () {
        if (!this.showEquipment()) return null;

        if (!this.model.get('equipment_location')) return _t('mobile_sensors_no_equipment_linked');

        const titleList = [];
        const equipment_location = this.model.get('equipment_location');

        if (equipment_location?.equipment_model_name) titleList.push(equipment_location.equipment_model_name);
        if (equipment_location?.location_zone) titleList.push(equipment_location.location_zone);
        if (equipment_location?.identifier) titleList.push(equipment_location.identifier);

        return titleList.join(' - ');
    },

    render_chart: function () {
        ZenputHighchartsLegacy.SparkLine = function (a, b, c) {
            var hasRenderToArg = typeof a === 'string' || a.nodeName,
                options = arguments[hasRenderToArg ? 1 : 0],
                defaultOptions = {
                    chart: {
                        renderTo: (options.chart && options.chart.renderTo) || this,
                        backgroundColor: null,
                        borderWidth: 0,
                        type: 'line',
                        margin: [2, 0, 2, 0],
                        height: 50,
                        style: {
                            overflow: 'visible'
                        },

                        // small optimalization, saves 1-2 ms each sparkline
                        skipClone: true
                    },
                    title: {
                        text: ''
                    },
                    credits: {
                        enabled: false
                    },
                    xAxis: { visible: false },
                    yAxis: { visible: false },
                    legend: {
                        enabled: false
                    },
                    tooltip: {
                        enabled: true
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            lineWidth: 1,
                            shadow: false,
                            states: {
                                hover: {
                                    lineWidth: 1
                                }
                            },
                            marker: {
                                radius: 1,
                                states: {
                                    hover: {
                                        radius: 2
                                    }
                                }
                            },
                            fillOpacity: 0.25
                        },
                        column: {
                            negativeColor: '#910000',
                            borderColor: 'silver'
                        }
                    }
                };

            options = ZenputHighchartsLegacy.merge(defaultOptions, options);

            return hasRenderToArg
                ? new ZenputHighchartsLegacy.Chart(a, options, c)
                : new ZenputHighchartsLegacy.Chart(options, b);
        };

        var $elems = $('div[data-sparkline]');

        // Creating 153 sparkline charts is quite fast in modern browsers, but IE8 and mobile
        // can take some seconds, so we split the input into chunks and apply them in timeouts
        // in order avoid locking up the browser process and allow interaction.
        function doChunk() {
            var time = new Date();
            var len = $elems.length;
            var $td;
            var stringdata;
            var arr;
            var data;
            var chart;

            for (var i = 0; i < len; i += 1) {
                $td = $($elems[i]);
                stringdata = String($td.data('sparkline'));
                arr = stringdata.split('; ');
                data = $.map(arr[0].split(','), parseFloat);
                chart = {};

                if (arr[1]) {
                    chart.type = arr[1];
                }

                var temperature_unit =
                    $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');

                $td.highcharts('SparkLine', {
                    series: [
                        {
                            data: data,
                            pointStart: 1,
                            tooltip: {
                                headerFormat: '',
                                pointFormat: `<b>{point.y}</b> °${temperature_unit}<br/>`
                            }
                        }
                    ],
                    chart: chart
                });

                // If the process takes too much time, run a timeout to allow interaction with the browser
                if (new Date() - time > 500) {
                    $elems.splice(0, i + 1);
                    setTimeout(doChunk, 0);
                    break;
                }
            }
        }
        doChunk();
    },

    _fetch_sparkline_data: function () {
        var this_ = this;
        this.sensor_sparkline = new SensorSparkline({ id: this.model.get('id') });
        // This cache: true is very important or the server side cache-control
        // will be ignored and we will send one request per table render per row
        this.sensor_sparkline.fetch({
            cache: true,
            success: this_.render
        });
    }
});

export const SensorsList = ComponentBodyViewTopItems.extend({
    itemView: SensorItem,
    collection: null // Sensors
});

export const GatewayItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_gateway_row',
    tagName: 'tr',

    templateHelpers: function () {
        return {
            gateway_status_class: get_device_status_class(this.model.get('is_enabled'), this.model.get('is_online')),
            gateway_status: get_device_status_text(this.model.get('is_enabled'), this.model.get('is_online'))
        };
    },

    _get_online_display: function () {
        return this.model.get('is_online') ? _t('mobile_sensors_device_online') : _t('mobile_sensors_device_offline');
    }
});

export const GatewaysList = ComponentBodyViewTopItems.extend({
    itemView: GatewayItem,
    collection: null // Gateways
});

export const DevicesLayout = Backbone.Marionette.Layout.extend({
    template: '#tpl_devices_layout',
    className: 'devices_layout',
    view_class: null,
    collection: null,
    action_button_title_key: null,
    show_action_button: true,
    allow_pagination: false,

    events: {
        'click .display_wizard': 'display_wizard'
    },

    regions: {
        r_devices: '.r_devices',
        r_sensors_devices_see_more: '.r_sensors_devices_see_more'
    },

    templateHelpers: function () {
        return {
            show_action_button: this.show_action_button,
            action_button_title: this.get_action_button_title()
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.view_class) {
            throw new Error('Programmer Error: You must set the view_class');
        }
    },

    onRender: function () {
        var device_list = new this.view_class({
            collection: this.collection,
            show_default_four_weeks_header: false
        });

        this.r_devices.show(device_list);

        var this_ = this;

        // remove see_more button when length reaches expected count
        this.listenTo(this.collection, 'sync', function () {
            this_._show_hide_see_more_devices_button();
        });
    },

    get_action_button_title: function () {
        return _t(this.action_button_title_key);
    },

    _show_hide_see_more_devices_button: function () {
        if (!this._should_show_see_more_devices()) {
            this.r_sensors_devices_see_more.close();
        } else if (!this.r_sensors_devices_see_more.currentView) {
            this.r_sensors_devices_see_more.show(
                new DevicesSeeMore({
                    collection: this.collection
                })
            );
        }
    },

    _should_show_see_more_devices: function () {
        if (!this.allow_pagination) {
            return false;
        }
        return this.collection.length !== this.collection.paginator.get('results_count');
    }
});

export const DevicesSeeMore = Backbone.Marionette.ItemView.extend({
    template: '#tpl_sensors_devices_see_more',
    tagName: 'a',
    className: 'tasks_bucket_see_more',
    collection: null, // Sensors

    attributes: {
        href: '#'
    },

    events: {
        click: 'fetch_more'
    },

    fetch_more: function (event) {
        var this_ = this;

        if (this.$el.hasClass('disabled')) {
            return false;
        }

        this._disable_button();
        this.collection.paginator.next_page();
        this.collection.fetch({
            remove: false,
            success: function () {
                this_._enable_button();
            },
            error: function () {
                this_._enable_button();

                this_.collection.paginator.previous_page();
                if (!isConnected()) {
                    showOfflineMessage();
                }
            }
        });

        return false;
    },

    _disable_button: function () {
        this.$el.addClass('disabled');
    },

    _enable_button: function () {
        this.$el.removeClass('disabled');
    }
});

export const SensorsLayout = DevicesLayout.extend({
    action_button_title_key: 'mobile_sensors_add_sensor',
    view_class: SensorsList,
    placements: null, // SensorsPlacements
    wizard_options: null,
    allow_pagination: true,

    initialize: function (options) {
        this.account = $f.mobile.dashboard.get_active_account_or_group();
        options = options || {};
        $.extend(this, options);
        this._fetch_initial_data();
    },

    onRender: function () {
        SensorsLayout.__super__.onRender.apply(this, arguments);

        this._set_sensor_button_state(false);
    },

    display_wizard: function () {
        $f.mobile.nav.show(this.wizard_options.wizard_class, {
            stepNumberKey: this.wizard_options.step_number_key,
            stepTitleKey: this.wizard_options.step_title_key,
            nextStepViewClass: this.wizard_options.next_step_view_class,
            type: this.wizard_options.type,
            selectedManufacturer: $f.mobile.sensors.util.get_manufacturer_sensor(this.account)
        });

        return false;
    },

    _set_sensor_button_state: function (is_enabled) {
        if (!is_enabled) {
            this.$el.find('.display_wizard').hide();
            return;
        }

        this.$el.find('.display_wizard').show();
        return;
    },

    _fetch_initial_data: async function () {
        const show_sensor_button = $f.mobile.sensors.util.can_add_sensors(this.account);
        try {
            await this._fetch_placements();
            this._set_sensor_button_state(show_sensor_button);
            this.wizard_options = this._get_wizard_options();
        } catch (error) {
            this._set_sensor_button_state(show_sensor_button);
        }
    },

    _fetch_placements: function () {
        this.placements = new SensorsPlacements();

        return new Promise((resolve, reject) => {
            this.placements.fetch({
                success: resolve,
                error: reject
            });
        });
    },

    _get_wizard_options: function () {
        const canSelectManufacturer = $f.mobile.sensors.util.can_select_manufacturer_sensor(this.account);

        const commonOptions = {
            step_number_key: 'mobile_sensors_wizard_step_one',
            type: 'sensor'
        };

        // PlacementStepView.displayNextStep() checks if next step after placemet should be
        // Equipment or DeviceName
        // Flow: Manufacturer -> Placement -> either (Equipment or DeviceName)
        if (canSelectManufacturer) {
            return {
                wizard_class: GatewayTypeStepView,
                next_step_view_class: PlacementStepView,
                ...commonOptions
            };
        }

        // PlacementStepView.displayNextStep() checks if next step after placemet should be
        // Equipment or DeviceName
        // Flow: Placement -> either (Equipment or DeviceName)
        return {
            wizard_class: PlacementStepView,
            ...commonOptions
        };
    },

    _are_company_placements_configured: function () {
        return this.placements.length !== 0;
    }
});

export const GatewaysLayout = DevicesLayout.extend({
    action_button_title_key: 'mobile_sensors_add_gateway',
    view_class: GatewaysList,
    wizard_options: null,

    initialize: function () {
        this.account = $f.mobile.dashboard.get_active_account_or_group();
        this.wizard_options = this._get_wizard_options();
    },

    _get_wizard_options: function () {
        const canSelectManufacturer = $f.mobile.sensors.util.can_select_manufacturer_gateway();
        const gatewayManufacturer = $f.mobile.sensors.util.get_manufacturer_gateway();
        const commonOptions = {
            step_number_key: 'mobile_sensors_wizard_step_one',
            type: 'gateway'
        };

        // Flow: Manufacturer -> GatewayMessage -> DeviceName
        if (canSelectManufacturer) {
            return {
                wizard_class: GatewayTypeStepView,
                next_step_view_class: GatewayMessageStepView,
                ...commonOptions
            };
        }

        // Flow: GatewayMessage -> DeviceName -> DeviceBarcode
        if (gatewayManufacturer === $f.mobile.sensors.util.MANUFACTURER.MONNIT) {
            return {
                wizard_class: GatewayMessageStepView,
                next_step_view_class: DeviceNameStepView,
                ...commonOptions
            };
        }

        // default case when cannot select manufacturer nor is not monnit
        // Flow: DeviceName -> DeviceBarcode
        return {
            wizard_class: DeviceNameStepView,
            next_step_view_class: DeviceBarcodeStepView,
            ...commonOptions
        };
    },

    display_wizard: function () {
        $f.mobile.nav.show(this.wizard_options.wizard_class, {
            type: this.wizard_options.type,
            nextStepViewClass: this.wizard_options.next_step_view_class,
            stepNumberKey: this.wizard_options.step_number_key,
            selectedManufacturer: $f.mobile.sensors.util.get_manufacturer_gateway()
        });
        return false;
    }
});

export const CooldownLayout = Backbone.Marionette.Layout.extend({
    template: '#tpl_cooldown_layout',
    className: 'devices_layout',
    collection: null, // Cooldowns

    events: {
        'click .start_cooldown': 'display_cooldown_page'
    },

    regions: {
        r_cooldowns: '.r_cooldowns'
    },

    templateHelpers: function () {
        return {
            action_button_title: _t('mobile_sensor_start_cooldown_process')
        };
    },

    onRender: function () {
        this._show_cooldowns();
    },

    display_cooldown_page: function () {
        $f.mobile.nav.show(CooldownCreator);
        return false;
    },

    _show_cooldowns: function () {
        this.r_cooldowns.show(
            new CooldownsList({
                collection: this.collection
            })
        );
    }
});

export const ComponentSensors = DashboardCardComponent.extend({
    query_collection_class: Sensors,
    loading_state_view_class: ComponentLoadingStateTableItem,
    query_key: null, // doesn't use the dashboard query system
    query_structure: { metadata: {} },
    view_class: SensorsLayout,

    setup_query: function () {
        ComponentSensors.__super__.setup_query.apply(this, arguments);
        this.account = $f.mobile.dashboard.get_active_account_or_group();
        this.query_collection.paginator.set({ account_id: this.account.id });
    },

    show_down_state: function () {
        this.r_down_state.show(
            new EmptyListMessage({
                icon_class: null,
                image_filename: 'sensor_downstate.svg',
                i18n_key_title: 'mobile_sensors_nothing_to_see_here',
                i18n_key_message: 'mobile_sensors_contact_manager',
                zenput_codes: this.zenput_code,
                allow_retry: this.allow_retry
            })
        );
    },

    show_body_view: function (state, response) {
        // In case the user has left this dashboard view by the time our request has finished
        if (this.isClosed) {
            return;
        }

        if (this.r_loading_state) {
            this.r_loading_state.reset();
        }

        this.trigger('query_collection_loaded');

        var is_collection_empty = response && response.length === 0;

        if (is_collection_empty && !this.account.get('has_gateways')) {
            this.show_down_state();
            this.r_body.reset();
            return;
        }

        this.r_down_state.reset();
        this.r_body.show(
            new this.view_class({
                collection: response,
                query_collection: this.query_collection,
                parent_component: this
            })
        );
    }
});

export const ComponentGateways = DashboardCardComponent.extend({
    query_collection_class: Gateways,
    loading_state_view_class: ComponentLoadingStateTableItem,
    query_key: null, // doesn't use the dashboard query system
    query_structure: { metadata: {} },
    view_class: GatewaysLayout,

    show_body_view: function (state, response) {
        // In case the user has left this dashboard view by the time our request has finished
        if (this.isClosed) {
            return;
        }

        if (this.r_loading_state) {
            this.r_loading_state.reset();
        }

        this.trigger('query_collection_loaded');

        var is_collection_empty = response && response.length === 0;

        if (is_collection_empty) {
            this.show_down_state();
            this.r_body.reset();
            return;
        }

        this.r_down_state.reset();
        this.r_body.show(
            new this.view_class({
                collection: response,
                query_collection: this.query_collection,
                parent_component: this
            })
        );
    },

    show_down_state: function (zenput_code) {
        this.r_down_state.show(new NoDevices());
    }
});

export const ComponentCooldown = DashboardCardComponent.extend({
    query_collection_class: Cooldowns,
    loading_state_view_class: ComponentLoadingStateTableItem,
    query_key: null, // doesn't use the dashboard query system
    query_structure: { metadata: {} },
    view_class: CooldownLayout,

    setup_query: function () {
        ComponentCooldown.__super__.setup_query.apply(this, arguments);
        var account = $f.mobile.dashboard.get_active_account_or_group();
        this.query_collection.paginator.set({ account_id: account.id });
    }
});

export const DashboardCardSensors = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_sensors_sensors');
    },

    components: [ComponentSensors]
});

export const DashboardCardGateways = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_sensors_gateways');
    },

    components: [ComponentGateways]
});

export const DashboardCardCooldown = MobileDashboardCard.extend({
    get_title: function () {
        return _t('mobile_sensor_cooldown_process');
    },

    get_subtitle: function () {
        return _t('mobile_sensor_last_12_hours');
    },

    components: [ComponentCooldown]
});

export const NoDevices = EmptyListMessage.extend({
    className: 'no_devices',
    icon_class: null,
    image_filename: 'gateway_downstate.svg',
    i18n_key_message: 'mobile_sensors_set_up_gateway_message',
    action_button_title: 'mobile_sensors_set_up_gateway',
    wizard_options: null,

    events: {
        'click .click_action': 'display_gateway_wizard'
    },

    initialize: function () {
        this.account = $f.mobile.dashboard.get_active_account_or_group();
        this.wizard_options = this._get_wizard_options();
    },

    _get_wizard_options: function () {
        const canSelectManufacturer = $f.mobile.sensors.util.can_select_manufacturer_gateway();
        const gatewayManufacturer = $f.mobile.sensors.util.get_manufacturer_gateway();
        const commonOptions = {
            step_number_key: 'mobile_sensors_wizard_step_one',
            type: 'gateway'
        };

        // Flow: Manufacturer -> GatewayMessage -> DeviceName
        if (canSelectManufacturer) {
            return {
                wizard_class: GatewayTypeStepView,
                next_step_view_class: GatewayMessageStepView,
                ...commonOptions
            };
        }

        // Flow: GatewayMessage -> DeviceName -> DeviceBarcode
        if (gatewayManufacturer === $f.mobile.sensors.util.MANUFACTURER.MONNIT) {
            return {
                wizard_class: GatewayMessageStepView,
                next_step_view_class: DeviceNameStepView,
                ...commonOptions
            };
        }

        // default case when cannot select manufacturer nor is not monnit
        // Flow: DeviceName -> DeviceBarcode
        return {
            wizard_class: DeviceNameStepView,
            next_step_view_class: DeviceBarcodeStepView,
            ...commonOptions
        };
    },

    display_gateway_wizard: function () {
        $f.mobile.nav.show(this.wizard_options.wizard_class, {
            type: this.wizard_options.type,
            nextStepViewClass: this.wizard_options.next_step_view_class,
            stepNumberKey: this.wizard_options.step_number_key,
            selectedManufacturer: $f.mobile.sensors.util.get_manufacturer_gateway()
        });
        return false;
    }
});

export const PlacementModal = BaseTypeaheadModal.extend({
    collection: new SensorsPlacements(),
    toolbarnav_title: function () {
        return _t('mobile_sensors_add_device');
    },

    format_result: function (item) {
        return item.get('name');
    }
});

export const PlacementTypeahead = MobileBaseTypeahead.extend({
    model: null, // SensorPlacement
    modal_class: PlacementModal,
    i18n_key_placeholder: 'mobile_sensors_select_placement',

    get_default_value: function () {
        return this.model ? this.model.get('name') : '';
    }
});

export const SensorSettingsNickname = Backbone.Marionette.ItemView.extend({
    template: '#tpl_sensor_settings_nickname',
    model: null, // SensorStatus
    placeholder: '',
    nickname: null, // string
    placements: null, // SensorPlacement Collection
    selectedPlacement: null, //SensorPlacement
    onSelect: function () {},
    onChange: function () {},
    placementUsesNicknames: function () {
        return this.selectedPlacement && this.selectedPlacement.get('sensor_placement_nicknames').length > 0;
    },

    templateHelpers: function () {
        return {
            nickname:
                this.placementUsesNicknames() && this.selectedPlacement.get('sensor_placement_nicknames').length > 1
                    ? this.parseNicknameValue()
                    : this.nickname,
            nicknames: this.selectedPlacement && this.selectedPlacement.get('sensor_placement_nicknames'),
            usesNicknames: this.placementUsesNicknames()
        };
    },

    events: {
        'change select': 'selectName',
        'keyup .name': 'keyInputChange'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    selectName: function (event) {
        const target = $(event.target);
        this.updateSensorName(target.attr('value'));
    },

    keyInputChange: function (event) {
        const $event_target = $(event.target);
        this.updateSensorName($event_target.val());
    },

    updateSensorName: function (nickname) {
        this.onChange(nickname);
    },

    parseNicknameValue: function () {
        // Removes the nickname number for multiple nicknames placements
        return this.nickname.split('-')[0].trim();
    }
});

export const SensorStatusSelect = Backbone.Marionette.ItemView.extend({
    template: '#tpl_sensor_status_select',
    sensor_status: null, // SensorsStatus
    model: null, // SensorStatus
    placeholder: 'null',
    on_select: function () {},

    templateHelpers: function () {
        return {
            sensor_status: this.sensor_status,
            selected_id: this.model.get('id')
        };
    },

    events: {
        'change select': 'bind_value'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    bind_value: function (event) {
        const target = $(event.target);
        this.model.set({ id: target.val(), name: target.attr('name') });
        this.on_select(this.model.get('id'));
    }
});

export const DeviceSuccess = Backbone.Marionette.Layout.extend({
    className: 'wizard',
    template: '#tpl_device_success_message_step',
    regions: {
        r_success_image: '.r_success_image'
    },
    onRender: function () {
        if (!window.customElements) {
            this.r_success_image.show(new StaticDeviceSuccessImage());
        } else {
            this.r_success_image.show(new AnimatedDeviceSuccessImage());
        }
    }
});

export const AnimatedDeviceSuccessImage = Backbone.Marionette.ItemView.extend({
    template: '#tpl_animated_device_success_image'
});

export const StaticDeviceSuccessImage = Backbone.Marionette.ItemView.extend({
    template: '#tpl_static_device_success_image'
});

export const ChartSettings = Backbone.Marionette.Layout.extend({
    template: '#tpl_chart_settings_component',
    triggers: [],

    toggleTrigger: function (triggerId) {
        const trigger = this.triggers.findWhere({ id: triggerId });
        const currentValue = trigger.get('active');
        trigger.set('active', !currentValue);
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onRender: function () {
        const menuWrapper = this.$el.find('.chart_settings')[0];

        window.mountReactJSX(menuWrapper, () => {
            const [activeTriggers, setActiveTriggers] = useState(this.triggers.toJSON());
            const [anchorEl, setAnchorEl] = useState(null);
            const open = Boolean(anchorEl);

            const handleClick = (event) => {
                setAnchorEl(event.currentTarget);
            };
            const handleClose = () => {
                setAnchorEl(null);
            };

            if (!activeTriggers || activeTriggers.length === 0) {
                return <></>;
            }

            return (
                <>
                    <Button onClick={handleClick} size="small" variant="text" startIcon={<Settings />}>
                        {_t('mobile_sensors_chart_settings')}
                    </Button>
                    <Menu open={open} anchorEl={anchorEl} onClose={handleClose} id="sensorChartSettings">
                        {activeTriggers.map((trigger) => (
                            <MenuItem
                                key={trigger.id}
                                onClick={() => {
                                    this.toggleTrigger(trigger.id);
                                    setActiveTriggers(this.triggers.toJSON());
                                }}
                            >
                                <ListItemText>
                                    <Typography variant="body2">{trigger.title}</Typography>
                                </ListItemText>
                                <ListItemIcon>
                                    {trigger.active ? <Visibility color="primary" /> : <VisibilityOff />}
                                </ListItemIcon>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            );
        });
    },

    onClose: function () {
        const menuWrapper = this.$el.find('.chart_settings')[0];
        window.unmountReact(menuWrapper);
    },

    onBeforeRender: function () {
        const menuWrapper = this.$el.find('.chart_settings')[0];
        if (menuWrapper) {
            window.unmountReact(menuWrapper);
        }
    }
});

export const ChartLegends = Backbone.Marionette.Layout.extend({
    template: '#tpl_chart_legends_component',
    triggersWithColors: [],

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onRender: function () {
        const chart_legends = this.$('.chart_legends')[0];

        window.mountReactJSX(chart_legends, () => (
            <Grid>
                {this.triggersWithColors.map((trigger) => (
                    <Button
                        key={trigger.get('id')}
                        size="small"
                        variant="text"
                        sx={{ '& span.MuiButton-startIcon': { marginRight: 0.5 }, maxWidth: '150px' }}
                        startIcon={<Stop htmlColor={trigger.get('plotLineColor')} />}
                    >
                        <Typography sx={{ textTransform: 'none' }} noWrap variant="caption" color="text.primary">
                            {trigger.get('title')}
                        </Typography>
                    </Button>
                ))}
            </Grid>
        ));
    },

    /**
     * Remove the React component from the DOM when this backbone view closes and before re-rendering
     */
    onClose: function () {
        const menuWrapper = this.$el.find('.chart_legends')[0];
        window.unmountReact(menuWrapper);
    },

    onBeforeRender: function () {
        const menuWrapper = this.$el.find('.chart_legends')[0];
        if (menuWrapper) {
            window.unmountReact(menuWrapper);
        }
    }
});

export const SensorDetailPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_sensor_detail_page',
    className: 'sensor_detail_page',
    toolbarnav_center_title: true,
    toolbarnav_show_edit_sensor: true,
    use_transitions: true,
    toolbarnav_title: function () {
        return _t('mobile_sensors_sensor');
    },
    selected_period: shared_sensors_utils.LOOKBACK_PERIODS.DAY,
    sparkline: null, // SensorSparkline
    triggers: null, // SensorTriggers
    settingsTriggers: null, //settingTriggers
    triggersWithColors: [], // TriggerWithColors
    model: null, // Sensor
    reading: null, // SensorReading
    last_reading_timeout: null,
    tooltip_timeout: 500,
    regions: {
        r_chart_settings: '.r_chart_settings',
        r_chart_legends: '.r_chart_legends'
    },

    events: {
        'click .lookback_period_1d': '_show_1d',
        'click .lookback_period_1w': '_show_1w',
        'click .lookback_period_1m': '_show_1m',
        'click .lookback_period_3m': '_show_3m',
        'click .lookback_period_6m': '_show_6m',
        'click .lookback_period_1y': '_show_1y'
    },

    templateHelpers: function () {
        var readings = this.sparkline.get('values');
        var converted_readings = $f.mobile.sensors.util.convert_graph_readings_to_company_temperature(readings);
        const { last_reading_date, last_reading_value } = this._get_last_reading_info();
        return {
            battery_level: this._get_sensor_battery_icon_class(),
            battery_shown_class: !$f.mobile.sensors.util.display_battery(this.model.get('battery'))
                ? 'battery-not-shown'
                : '',
            has_initialized: !this.model.is_initializing(),
            last_reading_date_formatted: last_reading_date,
            last_reading_value_formatted: last_reading_value,
            lookback_period_1d_formatted: _t('mobile_sensors_one_day'),
            lookback_period_1w_formatted: _t('mobile_sensors_one_week'),
            lookback_period_1m_formatted: _t('mobile_sensors_one_month'),
            lookback_period_3m_formatted: _t('mobile_sensors_three_months'),
            lookback_period_6m_formatted: _t('mobile_sensors_six_months'),
            lookback_period_1y_formatted: _t('mobile_sensors_one_year'),
            name: this.model.get('name'),
            sensor_status: get_device_status_text(this.model.get('is_enabled'), this.model.get('is_online')),
            sensor_status_class: get_device_status_class(this.model.get('is_enabled'), this.model.get('is_online')),
            online_display: this._get_online_display(),
            placement: this.model.get_placement_for_display(),
            selected_1d_class: this._get_selected_period_class('1d'),
            selected_1w_class: this._get_selected_period_class('1w'),
            selected_1m_class: this._get_selected_period_class('1m'),
            selected_3m_class: this._get_selected_period_class('3m'),
            selected_6m_class: this._get_selected_period_class('6m'),
            selected_1y_class: this._get_selected_period_class('1y'),
            sparkline_readings: converted_readings,
            sparkline_timestamps: this.sparkline.get('timestamps'),
            sparkline_lookback_period: this.sparkline.get('lookback_period'),
            triggers: this.triggers ? this.triggers.models : null
        };
    },

    initialize: async function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        await this.loadInitialData();
        this.reading = new SensorReading({
            value: this.model.get_last_reading_value(),
            timestamp: this.model.get_last_reading_date()
        });

        this.listenTo(this.reading, 'change', function () {
            this_._update_reading_display();
        });
        this.listenTo(this.settingTriggers, 'change', function () {
            this.triggersWithColors.reset(this.triggers.getTriggersWithColors(this.settingTriggers));
            this._render_chart();
        });
    },

    loadInitialData: async function () {
        showLoadingDialog();
        try {
            await this.fetchTriggers();
        } catch (err) {
            this.triggers = new SensorTriggers([], { id: this.model.get('id') });
        }
        hideLoadingDialog();

        const settingTriggers = this.triggers.filterOutEqual().map((trigger) => {
            return new SettingTrigger({
                title: trigger.get('title'),
                id: trigger.get('id'),
                active: true
            });
        });
        this.settingTriggers = new SettingTriggers(settingTriggers);
        this.triggersWithColors = new TriggersWithColors(this.triggers.getTriggersWithColors(this.settingTriggers));
        this._fetch_sparkline_data();
    },

    on_toolbarnav_init: function () {
        this.toolbarnav.set_sensor_model(this.model);
    },

    onRender: function () {
        this._render_chart();
        this.r_chart_settings.show(
            new ChartSettings({
                triggers: this.settingTriggers || new SensorTriggers([], { id: this.model.get('id') })
            })
        );
    },

    _get_last_reading_date_formatted: function (reading_date) {
        return this.model.is_initializing() ? '-' : `${_t('mobile_sensors_last_reading')} ${reading_date}`;
    },

    _get_last_reading_info: function () {
        return {
            last_reading_date: this._get_last_reading_date_formatted(this.model.get_last_reading_date()),
            last_reading_value: this.model.get_last_reading_value()
        };
    },

    fetchTriggers: function () {
        this.triggers = new SensorTriggers([], { id: this.model.get('id') });
        return new Promise((resolve, reject) => {
            this.triggers.fetch({
                success: resolve,
                error: reject
            });
        });
    },

    _fetch_sparkline_data: function () {
        this.sparkline = new SensorSparkline({ id: this.model.get('id') });
        this._show_1d();
    },

    _get_sensor_battery_icon_class: function () {
        if (!$f.mobile.sensors.util.display_battery(this.model.get('battery'))) {
            return '';
        }

        var battery = this.model.get('battery');
        var battery_level = $f.mobile.sensors.util.convert_sensor_battery_percent_to_battery_level(battery);
        return `df-battery-${battery_level}`;
    },

    _get_selected_period_class: function (period) {
        return this.selected_period === period ? 'selected' : '';
    },

    _get_online_display: function () {
        return this.model.get('is_online') ? _t('mobile_sensors_device_online') : _t('mobile_sensors_device_offline');
    },

    _generateBetweenPlotLines: (from, to, color) => {
        const lowerPlotline = {
            ...DEFAULT_PLOT_LINE_OPTIONS,
            value: from,
            color
        };

        const upperPlotLine = {
            ...DEFAULT_PLOT_LINE_OPTIONS,
            value: to,
            color
        };

        return [lowerPlotline, upperPlotLine];
    },

    _generatePlotLine: (value, color) => ({
        ...DEFAULT_PLOT_LINE_OPTIONS,
        value,
        color
    }),

    _getSensorChartPlotLines: function (triggersWithColors) {
        return triggersWithColors.reduce((plotLines, trigger) => {
            const criterion = trigger.get('temperatureCriterion');
            const operatorId = criterion.operator_id;
            const readings = criterion.value;
            const plotLineColor = trigger.get('plotLineColor');

            if (operatorId === OperatorIds.BETWEEN || operatorId === OperatorIds.NOT_BETWEEN) {
                const [from, to] = $f.mobile.sensors.util.convert_graph_readings_to_company_temperature(readings);
                return [...plotLines, ...this._generateBetweenPlotLines(from, to, plotLineColor)];
            }
            if (
                operatorId === OperatorIds.GREATER_THAN_EQUAL ||
                operatorId === OperatorIds.LESS_THAN_EQUAL ||
                operatorId === OperatorIds.GREATER_THAN ||
                operatorId === OperatorIds.LESS_THAN
            ) {
                const value = $f.mobile.sensors.util.convert_graph_reading_to_company_temperature(readings);
                const plotLine = this._generatePlotLine(value, plotLineColor);

                return [...plotLines, plotLine];
            }
            return plotLines;
        }, []);
    },

    _generateBetweenPlotBand: (from, to) => ({
        from,
        to,
        color: '#FFDFB5',
        zIndex: 1
    }),

    _generateNotBetweenPlotBand: (from, to, color) => {
        const upperPlotBand = {
            from: PLOT_BAND_LOWER_LIMIT,
            to: from,
            color,
            zIndex: PLOT_BAND_Z_INDEX
        };

        const lowerPlotBand = {
            from: to,
            to: PLOT_BAND_UPPER_LIMIT,
            color,
            zIndex: PLOT_BAND_Z_INDEX
        };

        return [upperPlotBand, lowerPlotBand];
    },

    _generateGreaterThanPlotBand: (value, color) => ({
        from: value,
        to: PLOT_BAND_UPPER_LIMIT,
        color,
        zIndex: PLOT_BAND_Z_INDEX
    }),

    _generateLessThanPlotBand: (value, color) => ({
        from: PLOT_BAND_LOWER_LIMIT,
        to: value,
        color,
        zIndex: PLOT_BAND_Z_INDEX
    }),

    _getPlotBands: function (triggersWithColors) {
        return triggersWithColors.reduce((plotBands, trigger) => {
            const criterion = trigger.get('temperatureCriterion');
            const operatorId = criterion.operator_id;
            const readings = criterion.value;
            const plotBandColor = trigger.get('plotBandColor');

            if (operatorId === OperatorIds.BETWEEN || operatorId === OperatorIds.NOT_BETWEEN) {
                const [from, to] = $f.mobile.sensors.util.convert_graph_readings_to_company_temperature(readings);
                const isBetweenOperator = operatorId === OperatorIds.BETWEEN;
                if (isBetweenOperator) {
                    return [...plotBands, this._generateBetweenPlotBand(from, to, plotBandColor)];
                }
                return [...plotBands, ...this._generateNotBetweenPlotBand(from, to, plotBandColor)];
            }
            if (operatorId === OperatorIds.GREATER_THAN_EQUAL || operatorId === OperatorIds.GREATER_THAN) {
                const value = $f.mobile.sensors.util.convert_graph_reading_to_company_temperature(readings);
                const greatherThanPlotBand = this._generateGreaterThanPlotBand(value, plotBandColor);

                return [...plotBands, greatherThanPlotBand];
            }
            if (operatorId === OperatorIds.LESS_THAN_EQUAL || operatorId === OperatorIds.LESS_THAN) {
                const value = $f.mobile.sensors.util.convert_graph_reading_to_company_temperature(readings);
                const lessThanPlotBand = this._generateLessThanPlotBand(value, plotBandColor);

                return [...plotBands, lessThanPlotBand];
            }
            return plotBands;
        }, []);
    },

    _getTimezoneOffsetCallback: function (account) {
        const timezone = account && account.get('time_zone');
        return (timestamp) => {
            if (!timezone) {
                return 0;
            }
            // Highcharts expects the TZ offset to be inverted UTC+9 -> -9 and UTC-6 -> +6
            const offset = -1 * getTimezoneOffset(timezone, timestamp);
            return millisecondsToMinutes(offset);
        };
    },

    _render_chart: function () {
        const TICK_INTERVAL_MAP = {
            [shared_sensors_utils.LOOKBACK_PERIODS.DAY]: 4 * 3600 * 1000, // four hours
            [shared_sensors_utils.LOOKBACK_PERIODS.WEEK]: 24 * 3600 * 1000, // one day
            [shared_sensors_utils.LOOKBACK_PERIODS.MONTH]: 7 * 24 * 3600 * 1000 // seven days
        };
        var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');
        var $td = $($('.sensor_detail div[data-sparkline-readings]')[0]);
        var readings = $.map(String($td.data('sparkline-readings')).split(','), parseFloat);
        const timestamps = this.sparkline.get('timestamps');
        const lookback_period = this.sparkline.get('lookback_period');
        const getTzOffset = this._getTimezoneOffsetCallback(this.model.get('account'));
        var chart = {};
        var this_ = this;

        const plotBands = this._getPlotBands(this.triggersWithColors);
        const plotLines = this._getSensorChartPlotLines(this.triggersWithColors);

        const data = readings
            .map((reading, i) => {
                if (!isNaN(reading)) {
                    return [timestamps[i], reading];
                }

                return null;
            })
            .filter(Boolean);

        ZenputHighchartsLegacy.setOptions({
            global: {
                getTimezoneOffset: getTzOffset
            }
        });

        ZenputHighchartsLegacy.SparkLine = function (a, b, c) {
            var hasRenderToArg = typeof a === 'string' || a.nodeName,
                options = arguments[hasRenderToArg ? 1 : 0],
                defaultOptions = {
                    chart: {
                        renderTo: (options.chart && options.chart.renderTo) || this,
                        backgroundColor: null,
                        borderWidth: 0,
                        type: 'line',
                        height: 250,
                        style: {
                            overflow: 'visible'
                        },
                        // small optimalization, saves 1-2 ms each sparkline
                        skipClone: true
                    },
                    title: {
                        text: data.length ? '' : _t('mobile_dashboard_zenput_code_no_data_in_range')
                    },
                    credits: {
                        enabled: false
                    },
                    xAxis: {
                        crosshair: {
                            color: '#7c8388'
                        },
                        type: 'datetime',
                        visible: true,
                        dateTimeLabelFormats: {
                            minute: '%l :%P',
                            hour: '%l %P',
                            day: '%b %e',
                            week: '%b %e'
                        },
                        tickInterval: TICK_INTERVAL_MAP[lookback_period]
                    },
                    yAxis: {
                        gridLineColor: '#F2F7FA',
                        labels: { format: '{value}°' },
                        tickColor: '#FF0000',
                        tickInterval: temperature_unit === 'C' ? 10 : 15,
                        title: { text: undefined },
                        plotBands: plotBands,
                        plotLines,
                        opposite: true
                    },
                    legend: {
                        enabled: false
                    },
                    tooltip: {
                        backgroundColor: '#222C3F',
                        borderWidth: 0,
                        enabled: true,
                        useHTML: true,
                        hideDelay: this_.tooltip_timeout,
                        formatter: function () {
                            const sparkline_timestamp = timestamps[this.point.index];
                            const sparkline_reading = this_._get_reading_from_graph_point(this.y);
                            const sparkline_timestamp_formatted = this_._get_formatted_timestamp(sparkline_timestamp);
                            return `${sparkline_reading}<br/>${sparkline_timestamp_formatted}`;
                        },
                        positioner: function (labelWidth, _labelHeight, point) {
                            var chartWidth = $('.sensor_detail > .chart')[0].getBoundingClientRect().width;
                            // @author:jwells NOTE: Plot's X is offset by 44 pixels due to width of the Y axis. We could set this value
                            // explicitly, but that could cut off some fonts/browsers. This value, while hardcoded, is going
                            // to be close enough to all possible values in the wild. It lines up acceptably in all cases
                            // I tried and does not cause any clipping.
                            return {
                                x: Math.max(0, Math.min(point.plotX + 44 - labelWidth / 2, chartWidth - labelWidth)),
                                y: 0
                            };
                        },
                        shadow: false,
                        style: {
                            color: '#FFFFFF'
                        }
                    },
                    plotOptions: {
                        series: {
                            animation: false,
                            lineWidth: 2,
                            shadow: false,
                            states: {
                                hover: {
                                    lineWidth: 2
                                }
                            },
                            marker: {
                                radius: 1,
                                states: {
                                    hover: {
                                        radius: 2
                                    }
                                }
                            },
                            fillOpacity: 0.25,
                            point: {
                                events: {
                                    mouseOut: function () {
                                        /* using timeout to hide reset to last reading becauase
                                         *  Highcharts does not provide a callback when the tooltip hides
                                         */
                                        if (this_.last_reading_timeout) {
                                            clearTimeout(this_.last_reading_timeout);
                                        }

                                        var callback = this_._reset_to_last_reading_value.bind(this_);
                                        this_.last_reading_timeout = setTimeout(callback, this_.tooltip_timeout);
                                    }
                                }
                            },
                            gapSize: 1
                        }
                    },
                    trackByArea: false
                };

            options = ZenputHighchartsLegacy.merge(defaultOptions, options);

            return hasRenderToArg
                ? new ZenputHighchartsLegacy.Chart(a, options, c)
                : new ZenputHighchartsLegacy.Chart(options, b);
        };

        const now = Date.now();
        const minimum_timestamp_calculated = shared_sensors_utils.get_minimum_timestamp_for_lookback(
            this.selected_period,
            now
        );
        const first_reading_timestamp = data.length ? data[0][0] : now;
        $td.highcharts('SparkLine', {
            series: [
                {
                    data: data
                }
            ],
            chart: chart,
            xAxis: {
                max: now,
                min: shared_sensors_utils.get_minimum_timestamp_for_sparkline(
                    minimum_timestamp_calculated,
                    first_reading_timestamp
                )
            }
        });

        if (data.length) {
            this.r_chart_legends.show(new ChartLegends({ triggersWithColors: this.triggersWithColors }));
        }
    },

    _show_sparkline: function (period) {
        this.selected_period = period;
        this.sparkline.set_lookback_period(this.selected_period);
        this.sparkline.fetch({
            cache: true,
            success: this.render
        });
    },

    _show_1d: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.DAY);
    },

    _show_1w: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.WEEK);
    },

    _show_1m: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.MONTH);
    },

    _show_3m: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.THREE_MONTHS);
    },

    _show_6m: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.SIX_MONTHS);
    },

    _show_1y: function () {
        this._show_sparkline(shared_sensors_utils.LOOKBACK_PERIODS.YEAR);
    },

    _update_reading_display: function () {
        var $last_reading_date = $('.sensor_detail > .last_reading > .date');
        var $last_reading_value = $('.sensor_detail > .last_reading > .value');
        var timestamp = this.reading.get('timestamp');
        var display_last_reading = timestamp === this.model.get_last_reading_date();
        var timestap_display = display_last_reading ? `${_t('mobile_sensors_last_reading')} ${timestamp}` : timestamp;

        $last_reading_value.html(this.reading.get('value'));
        $last_reading_date.text(timestap_display);
    },

    _on_graph_mouse_over: function (point, timestamps) {
        var reading_timestamp = timestamps[point.index];
        var reading_value = this._get_reading_from_graph_point(point.y);
        var formatted_timestamp = this._get_formatted_timestamp(reading_timestamp);

        if (reading_value === this.model.get_last_reading_value()) {
            return;
        }

        this.reading.set({
            value: reading_value,
            timestamp: formatted_timestamp
        });
    },

    _reset_to_last_reading_value: function () {
        this.reading.set({
            value: this.model.get_last_reading_value(),
            timestamp: this._get_last_reading_date_formatted(this.model.get_last_reading_date())
        });
    },

    _get_reading_from_graph_point: function (reading) {
        var temperature_unit = $f.active_user.get('temperature_unit') ?? $f.active_company.get('temperature_unit');
        var display_unit = temperature_unit === 'C' ? shared_sensors_utils.CENTIGRADE : shared_sensors_utils.FAHRENHEIT;

        return `${reading} ${display_unit}`;
    },

    _get_formatted_timestamp: function (timestamp) {
        return shared_sensors_utils.get_formatted_sensor_timestamp({
            timestamp: timestamp,
            format: $f.utils.DATE_AND_TIME_NO_TZ_FMT,
            account: this.model.get('account')
        });
    }
});

export const SensorSettingsPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_sensor_settings_page',
    className: 'sensor_settings_page',
    toolbarnav_center_title: true,
    toolbarnav_show_add_task: false,
    use_transitions: true,
    toolbarnav_title: function () {
        return _t('mobile_sensors_sensor_settings');
    },
    model: null, // Sensor
    placements: null, //SensorPlacements

    events: {
        'click .remove': '_remove_sensor',
        'click .save': '_save_sensor'
    },

    regions: {
        r_placement_typeahead: '.r_placement_typeahead',
        r_status_select: '.r_status_select',
        r_sensor_settings_nickname: '.r_sensor_settings_nickname'
    },

    templateHelpers: function () {
        return {
            can_remove: $f.active_user.is_admin() || $f.active_user.is_manager(),
            id_number: this.model.get('sensor_id'),
            id_number_title: _t('mobile_sensors_id_number', { untranslatedID: 'ID' }),
            name: this.model.get('name')
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.model.store();
        this.placements = new SensorsPlacements();
    },

    onRender: function () {
        this._display_placement_typeahead();
        this._displaySensorSettingsNickname();
        this._display_status_select();
    },

    go_back: function () {
        this.model.restore();

        SensorSettingsPage.__super__.go_back.apply(this, arguments);
    },

    _display_placement_typeahead: function () {
        var this_ = this;

        this.r_placement_typeahead.show(
            new PlacementTypeahead({
                className: 'sensor_placement',
                model: this.model.get('placement'),
                post_on_select: function (placement) {
                    this_.model.set('placement', placement);
                    this_.render();
                    this_._show_save();
                },
                on_clear: function () {
                    this_.model.set('placement', '');
                    this_.render();
                    this_._show_save();
                }
            })
        );
    },

    _displaySensorSettingsNickname: function () {
        const this_ = this;
        this.placements.fetch({
            remove: false,
            success: function () {
                this_.r_sensor_settings_nickname.show(
                    new SensorSettingsNickname({
                        nickname: this_.model.get('name'),
                        selectedPlacement: this_.model.get('placement'),
                        placements: this_.placements,
                        onChange: function (name) {
                            this_._updateName(name);
                        }
                    })
                );
            },
            error: function () {
                showErrorMessage();
            }
        });
    },

    _display_status_select: function () {
        const id = this.model.get('is_enabled');
        const currentStatus = new SensorStatus({ id, name: id ? _t('mobile_pso_filter_active') : _t('Disabled') });
        const statusCollection = new SensorsStatus([
            { id: 1, name: _t('mobile_pso_filter_active') },
            { id: 0, name: _t('Disabled') }
        ]);

        this.r_status_select.show(
            new SensorStatusSelect({
                sensor_status: statusCollection,
                model: currentStatus,
                on_select: (status) => {
                    if (!status) {
                        this.model.set('is_enabled', null);
                        return this._show_save(false);
                    }

                    this.model.set('is_enabled', status);
                    this._show_save();
                }
            })
        );
    },

    _remove_sensor: function () {
        var this_ = this;

        confirmNotification(
            _t('mobile_sensors_remove_confirm_message'),
            function (success) {
                if (!success) {
                    return;
                }

                var account = $f.mobile.dashboard.get_active_account_or_group();
                // @author:jwells Save is used here to make sure the entity is sent as a post
                this_.model.save(
                    {},
                    {
                        type: 'DELETE',
                        use_id_param: true,
                        success: function () {
                            hideLoadingDialog();
                            $f.mobile.nav.go_back_to_view(MobileDashboard, {
                                active_account_or_group: account,
                                active_tab_id: 'sensors',
                                do_not_set_leftnav: true,
                                reset_active_group_or_account_on_close: false
                            });
                        },
                        error: function (_xhr, res) {
                            hideLoadingDialog();
                            var message = $f.mobile.sensors.util.get_error_string_from_response(res);
                            showErrorMessage(message);
                        }
                    }
                );
            },
            _t('mobile_sensors_remove_device'),
            _t('mobile_common_button_yes'),
            _t('mobile_common_button_no')
        );
    },

    _save_sensor: function () {
        showLoadingDialog();
        const fieldsToPatch = {
            name: this.model.get('name'),
            placement: this.model.get('placement'),
            is_enabled: this.model.get('is_enabled')
        };

        this.model.save(fieldsToPatch, {
            patch: true,
            use_id_param: true,
            type: 'PATCH',
            success: () => {
                hideLoadingDialog();
                showSuccessMessage(_t('mobile_message_settings_success_saved'));
                this._show_save(false);
                this.model.clear_history();
            },
            error: (_xhr, res) => {
                hideLoadingDialog();
                showErrorMessage($f.mobile.sensors.util.get_error_string_from_response(res));
            }
        });
    },

    _show_save: function (flag) {
        const $save_button = $('.sensor_settings_page > .save');

        if (!this.model.isValid()) {
            const error = this.model.validationError;
            showErrorMessage(`${error.field}: ${error.message}`);
            return $save_button.hide();
        }

        if (flag || typeof flag === 'undefined') {
            $save_button.show();
        } else {
            $save_button.hide();
        }
    },

    _updateName: function (name) {
        this.model.set('name', name);
        this._show_save();
    }
});

export const CooldownSensorModal = BaseTypeaheadModal.extend({
    collection: null, // Sensors
    toolbarnav_title: function () {
        return _t('mobile_sensors_add_device');
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        this.collection = new Sensors();
        var account = $f.mobile.dashboard.get_active_account_or_group();
        this.collection.paginator.set({ account_id: account.id });

        CooldownSensorModal.__super__.initialize.apply(this, arguments);
    },

    format_result: function (item) {
        return item.get('name');
    },

    transform_collection: function (results) {
        return results.filter(function (model) {
            return !model.get('in_use');
        });
    }
});

export const CooldownSensorsTypeahead = MobileBaseTypeahead.extend({
    model: null, // Sensor
    modal_class: CooldownSensorModal,
    i18n_key_placeholder: 'mobile_sensor_cooldown_select_sensor',

    get_default_value: function () {
        return this.model ? this.model.get('name') : '';
    }
});

export const CooldownCreator = BaseToolbarNavPageLayout.extend({
    template: '#tpl_cooldown_creator',
    toolbarnav_center_title: true,
    toolbarnav_show_add_task: false,
    use_transitions: true,
    toolbarnav_title: function () {
        return _t('mobile_sensor_cooldown_cool_down');
    },
    model: null, // Cooldown

    events: {
        'keyup .food .food_input': 'food_changed',
        'click .start_cooldown': 'start_cooldown'
    },

    regions: {
        r_sensors_typeahead: '.sensors_typeahead'
    },

    templateHelpers: function () {
        return {
            food_name: this.model.get('food'),
            cooldown_description: _t('mobile_sensor_cooldown_process_description', {
                stage1_start_temperature: $f.mobile.sensors.util.get_cooldown_stage_one_starting_temperature(),
                stage1_end_temperature: $f.mobile.sensors.util.get_cooldown_stage_one_ending_temperature(),
                stage2_end_temperature: $f.mobile.sensors.util.get_cooldown_stage_two_ending_temperature(),
                interpolation: { escapeValue: false }
            })
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.model) {
            this.model = new Cooldown();
        }
        // TODO: @jira IOT-228 check validation on clicking start cool down
        Backbone.Validation.bind(this, {
            valid: function (view, attr, selector) {
                var $valid_field = attr === 'food' ? view.$('.food_input') : view.$('.fake_input');
                $valid_field.removeClass('error');
            },
            invalid: function (view, attr, error, selector) {
                var $invalid_field = attr === 'food' ? view.$('.food_input') : view.$('.fake_input');
                $invalid_field.addClass('error');
                showErrorMessage(error);
            }
        });
    },

    onRender: function () {
        this.show_sensors_typeahead();
    },

    food_changed: function (event) {
        var target = $(event.target);
        this.model.set('food', target.val());
    },

    show_sensors_typeahead: function () {
        var this_ = this;

        this.r_sensors_typeahead.show(
            new CooldownSensorsTypeahead({
                className: 'cool_down_sensor_typeahead',
                model: this_.model.get('sensor'),
                post_on_select: function (sensor) {
                    this_.model.set('sensor', sensor);
                    this_.render();
                },
                on_clear: function () {
                    this_.model.set('sensor', '');
                }
            })
        );
    },

    start_cooldown: function () {
        if (!this.model.isValid(true)) {
            return;
        }
        showLoadingDialog();

        this.model.save(
            {},
            {
                type: 'POST',
                success: function () {
                    hideLoadingDialog();
                    $f.mobile.nav.go_back();
                },
                error: function () {
                    hideLoadingDialog();
                    showErrorMessage();
                }
            }
        );
    }
});

export const CooldownStageItemViewForTask = Backbone.Marionette.ItemView.extend({
    template: '#tpl_cooldown_stage_item_task',
    tagName: 'tr',
    model: null, // CooldownStage
    className: 'stage_item',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        return {
            name: this.model.get_stage_title()
        };
    }
});

export const CooldownStageListViewForTask = ComponentBodyViewTopItems.extend({
    itemView: CooldownStageItemViewForTask,
    collection: null, // CooldownStages
    show_default_four_weeks_header: false,

    getEmptyView: function () {
        return EmptyListMessage.extend({
            icon_class: null,
            i18n_key_message: 'mobile_sensors_cooldown_starting_temperature_out_of_range'
        });
    }
});

export const CooldownStageItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_cooldown_stage_item',
    tagName: 'tr',
    className: 'stage_item',
    model: null, // CooldownStage
    cooldown: null,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    templateHelpers: function () {
        return {
            name: this.model.get_stage_title(),
            date_reading: this.model.get_date_reading(),
            duration: this.model.get_duration(this.cooldown)
        };
    }
});

export const CooldownStageList = ComponentBodyViewTopItems.extend({
    itemView: CooldownStageItem,
    show_default_four_weeks_header: false,
    collection: null, // CooldownStages
    cooldown: null,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    itemViewOptions: function (model) {
        return {
            cooldown: this.cooldown
        };
    }
});

export const CooldownStatus = Backbone.Marionette.ItemView.extend({
    template: '#tpl_cooldown_status',
    model: null, // Cooldown

    templateHelpers: function () {
        return {
            title: this.model.get_status_title(),
            date_ended: this.model.get_date_ended(),
            duration: this.model.get_duration(),
            reading: this.model.get_failing_reading()
        };
    }
});

export const CooldownDetailPage = BaseToolbarNavPageLayout.extend({
    template: '#tpl_cooldown_detail_page',
    className: 'cooldown_detail_page',
    toolbarnav_center_title: true,
    use_transitions: true,
    toolbarnav_title: function () {
        return _t('mobile_sensor_cooldown_cool_down');
    },
    model: null, // Cooldown

    regions: {
        r_stages: '.stages',
        r_status: '.status'
    },

    templateHelpers: function () {
        return {
            started_cooldown: _t('mobile_sensors_cooldown_started_cooldown_process'),
            date_started: this.model.get_date_started(),
            sensor_name: this.model.get('sensor').get('name')
        };
    },

    onRender: function () {
        this._show_stages();

        if (this.model.get('date_ended')) {
            this._show_status();
        }
    },

    _show_stages: function () {
        this.r_stages.show(
            new CooldownStageList({
                collection: this.model.get('stages'),
                cooldown: this.model
            })
        );
    },

    _show_status: function () {
        this.r_status.show(
            new CooldownStatus({
                model: this.model
            })
        );
    }
});

export const DashboardTabSensors = BaseDashboardTab.extend({
    template: '#tpl_dashboard_sensors',
    account_gateways: null, // Gateways
    account: null, // Account

    regions: {
        r_body: '.dashboard_tab_body',
        r_empty: '.dashboard_tab_empty'
    },

    initialize: function () {
        DashboardTabSensors.__super__.initialize.apply(this, arguments);
        this.account = $f.mobile.dashboard.get_active_account_or_group();
        this.listenTo($f.application.vent, 'app:orientation_change', () => {
            $f.mobile.update_page_scrollable_height(this.$el);
        });
    },

    onRender: function () {
        if ($f.mobile.sensors.util.can_add_gateways() && this._has_no_gateways_or_sensors()) {
            this._show_no_gateways_view();
            return;
        }

        if (this._has_no_gateways_or_sensors()) {
            /* show this view after first rendering */
            this._show_no_integrations_view();
            return;
        }

        this.r_empty.close();
        DashboardTabSensors.__super__.onRender.apply(this, arguments);
    },

    _has_no_gateways_or_sensors: function () {
        return !this.account.get('has_gateways') && !this.account.get('has_sensors');
    },

    _show_no_gateways_view: function () {
        this.r_body.close();
        this.r_empty.show(new NoDevices());
    },

    _show_no_integrations_view: function () {
        this.r_body.close();
        this.r_empty.show(
            new EmptyListMessage({
                icon_class: null,
                image_filename: 'sensor_downstate.svg',
                i18n_key_title: 'mobile_sensors_missing_integration',
                i18n_key_message: 'mobile_sensors_contact_zenput_admin',
                zenput_codes: this.zenput_code,
                allow_retry: this.allow_retry
            })
        );
    },

    setup_cards: function () {
        this.dashboard_cards = new DashboardViewConfigurations();

        if ($f.active_company.has_feature('sensor_cool_down')) {
            this.dashboard_cards.add({
                view_class: DashboardCardCooldown
            });
        }

        this.dashboard_cards.add({
            view_class: DashboardCardSensors
        });

        if ($f.mobile.sensors.util.can_add_gateways()) {
            this.dashboard_cards.add({
                view_class: DashboardCardGateways
            });
        }

        return;
    },

    on_tab_focused: function () {
        showLoadingDialog();
        this._update_cards_display();
    },

    _update_cards_display: function () {
        this.setup_cards();
        hideLoadingDialog();
        this.render();
    }
});
